<script>
	import { LayerCake, Svg, Html, flatten } from "layercake";

	import { scaleOrdinal } from "d3-scale";
	import { timeParse } from "d3-time-format";

	import MultiLine from "./Lines/MultiLine.svelte";
	import Axes from "./Axes/Axes.svelte";
	import ValueLabel from "./Labels/ValueLabel.svelte";
	import SeriesLabel from "./Labels/SeriesLabel.svelte";
	import RangeAnnotations from "./Annotations/RangeAnnotations.svelte";
	import PointAnnotations from "./Annotations/PointAnnotations.svelte";
	import Trendline from "./Lines/Trendline.svelte";

	// This example loads csv data as json using @rollup/plugin-dsv
	import data from "../data/by_month_topic.csv";
	import { groupBy } from "../../../utils";

	const xKeyCast = timeParse("%Y-%m");

	// export let series;
	// export let dataValue;
	// export let yTicks;
	export let allYTicks;
	export let xDomain;
	export let noAnimation;
	export let info;

	/* --------------------------------------------
	 * Set what is our x key to separate it from the other series
	 */
	const xKey = "year_month";
	const yKey = "count";
	const zKey = "topic";

	const byTopic = groupBy(data, "topic");

	$: LINE_DRAW_DURATION = noAnimation ? 0 : 2000;

	// const seriesNames = Object.keys(data[0]).filter(d => d !== xKey);
	$: seriesColors = info.series.map((d) => d.color);

	/* --------------------------------------------
	 * Cast values
	 */

	let lastTopics = [];
	let yTicks = [];
	let groupedData;
	$: {
		groupedData = info.series.map(({ topic, id, ...rest }) => ({
			id,
			topic,
			settings: rest,
			values: byTopic[topic].map((d) => ({
				year_month: xKeyCast(d.year_month),
				count: info.dataValue === "count" ? +d.count : +d[info.dataValue] / 1000,
				topic: d.topic,
			})),
		}));

		// to fix the timing issue with the y-axis: the problem was that the y-axis was not updated at the same time when the data was updated
		// this lead to the line jumping to the new y-axis positions and then tweening to the desired data points.
		// the problem only occured when the previous topmost y-tick value was smaller than the current topmost y-tick value
		// for that reason, we delay the update of the y-axis by 50ms to avoid the jumping.
		// And we do that only if one or more topics stay the same (currentTopics vs. lastTopics --> hasCommonElements) to avoid setting the path of the line too many times which triggers the drawing animation.
		// Very hacky but it works.
		const currentTopics = info.series.map((s) => s.topic);
		const hasCommonElements = currentTopics.some((topic) => lastTopics.includes(topic));

		if (hasCommonElements && info.yTicks[info.yTicks.length - 1] < yTicks[yTicks.length - 1]) {
			setTimeout(() => {
				yTicks = info.yTicks;
			}, 80);
		} else {
			yTicks = info.yTicks;
		}
		lastTopics = currentTopics;
	}

	$: title = info.dataValue === "count" ? "Demonstrationen" : "Teilnehmende";

	let renderedLines = {};
</script>

<div class="chart-container">
	<div class="title-container">
		<span class="strong">{title}</span>
		<span>pro Monat in Berlin</span>
	</div>
	<LayerCake
		padding={{ top: 20, right: 0, bottom: 20, left: 40 }}
		x={xKey}
		y={yKey}
		z={zKey}
		xDomain={xDomain || [xKeyCast("2018-01"), xKeyCast("2023-12")]}
		yDomain={[0, yTicks[yTicks.length - 1]]}
		zScale={scaleOrdinal()}
		zRange={seriesColors}
		flatData={flatten(groupedData, "values")}
		data={groupedData}
	>
		<Svg label="blaaaaa">
			<Axes dataValue={info.dataValue} {yTicks} {allYTicks} />
		</Svg>
		<Svg label="blaaaaa">
			{#each groupedData as group, i (group.id || group.topic)}
				<MultiLine
					{group}
					lineDrawDuration={LINE_DRAW_DURATION}
					bind:rendered={renderedLines[group.id || group.topic]}
				/>
			{/each}
			<!-- bind:this={renderedLines[group.topic]} -->
		</Svg>

		<Html>
			<div class="edge-gradient left" class:visible={!!xDomain} />
			<PointAnnotations lineDrawDuration={LINE_DRAW_DURATION} {renderedLines} />
			<RangeAnnotations lineDrawDuration={LINE_DRAW_DURATION} />
			<SeriesLabel series={info.series} lineDrawDuration={LINE_DRAW_DURATION} />
			<ValueLabel dataValue={info.dataValue} lineDrawDuration={LINE_DRAW_DURATION} class="chart_label" />
			<div class="edge-gradient right" class:visible={!!xDomain} />
		</Html>
	</LayerCake>
</div>

<style lang="scss">
	.chart-container {
		position: relative;

		margin: var(--chart-margin);
		width: var(--chart-width);
		height: var(--chart-height);
	}

	.title-container {
		margin-bottom: 5px;
		span {
			color: #fff;
			opacity: 0.8;
			font-size: 16px;
		}
		.strong {
			font-weight: bolder;
			border-radius: 20px;
			background: #39355e;
			padding: 4px 7px;
		}
	}

	.edge-gradient {
		content: "";
		position: absolute;
		z-index: 10;
		top: -1px;
		height: 101%;
		width: 10%;
		background: linear-gradient(270deg, #211c46 0%, rgba(33, 28, 70, 0) 100%);
		display: none;

		&.visible {
			display: block;
		}

		&.left {
			left: 0;
			transform: scaleX(-1);
		}

		&.right {
			right: 0;
		}
	}

	:global(.layercake-layout-svg:first-of-type) {
		overflow: hidden !important;
	}
</style>
